import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, KeyRound, Loader2, UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { creerCompteSponsor } from '../services/api';
import { nomFamille, prenomUsage } from '../utils/nom';

/**
 * Création du compte sponsor, préalable au formulaire de parrainage.
 *
 * Le compte permet au sponsor de retrouver son dossier, ses devis et ses
 * factures. Il ne porte que le rôle Sponsor, sans accès à la gestion.
 */
export default function InscriptionSponsor({ onCree }: { onCree: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    prenom: '',
    nom: '',
    email: '',
    password: '',
    passwordConfirmation: '',
  });
  const [erreur, setErreur] = useState<string | null>(null);

  const creer = useMutation({
    mutationFn: () => creerCompteSponsor(form),
    onSuccess: () => {
      // La session vient de changer : l'identité en cache est périmée.
      queryClient.invalidateQueries({ queryKey: ['me'] });
      onCree();
    },
    onError: (e: Error) => setErreur(e.message),
  });

  const motsDePasseDifferents =
    form.passwordConfirmation !== '' && form.password !== form.passwordConfirmation;

  const invalide =
    !form.email.trim() ||
    form.password.length < 8 ||
    motsDePasseDifferents ||
    form.passwordConfirmation === '';

  return (
    <section className="card space-y-4 lg:p-6">
      <div className="flex items-start gap-3">
        <UserPlus className="w-6 h-6 text-primary-600 shrink-0 mt-0.5" />
        <div>
          <h2 className="font-semibold lg:text-lg">Créez votre compte sponsor</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Il vous permettra de suivre votre demande, vos devis et vos factures.
          </p>
        </div>
      </div>

      {erreur && (
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3 text-sm">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <p className="flex-1">{erreur}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <input
          className="input"
          placeholder="Prénom (facultatif)"
          value={form.prenom}
          onChange={(e) => setForm({ ...form, prenom: prenomUsage(e.target.value) })}
        />
        <input
          className="input"
          placeholder="Nom (facultatif)"
          value={form.nom}
          onChange={(e) => setForm({ ...form, nom: nomFamille(e.target.value) })}
        />
        <input
          className="input lg:col-span-2"
          type="email"
          autoComplete="email"
          placeholder="Adresse e-mail — elle servira d'identifiant"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <input
          className="input"
          type="password"
          autoComplete="new-password"
          placeholder="Mot de passe (8 caractères minimum)"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
        />
        <input
          className="input"
          type="password"
          autoComplete="new-password"
          placeholder="Confirmez le mot de passe"
          value={form.passwordConfirmation}
          onChange={(e) => setForm({ ...form, passwordConfirmation: e.target.value })}
        />
      </div>

      {motsDePasseDifferents && (
        <p className="text-xs text-red-600">Les deux mots de passe ne correspondent pas.</p>
      )}

      <button
        onClick={() => {
          setErreur(null);
          creer.mutate();
        }}
        disabled={invalide || creer.isPending}
        className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
      >
        {creer.isPending ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <KeyRound className="w-4 h-4" />
        )}
        Créer mon compte et continuer
      </button>

      <p className="text-sm text-gray-600 dark:text-gray-400">
        Vous avez déjà un compte ?{' '}
        <Link to="/login" className="text-primary-700 hover:underline">
          Connectez-vous
        </Link>{' '}
        pour poursuivre votre demande.
      </p>
    </section>
  );
}
